//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CampaignRequestProductSource,
  CreatorCampaignsRequestProductVariant,
} from '@apis/campaign-requests/creators/CreatorsCampaignRequestApiModel';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  CreatorCampaignProductStoreType,
  CreatorsCampaignInfluencerCommissionType,
  FixedPay,
  InfluencersCommission,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApiModel';
import { BasicCreatorsInfo } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import { ProfileNetwork } from '@influencers/constants/influencersConstants';
import { SocialCreatorsPlatforms } from '@models/CreatorsTypes';
import DateString from '@models/DateString';
import { SourceType } from '@models/InfluencerSourceType';
import ItemId from '@models/ItemId';
import { PaginatedResponse } from '@models/PaginationTypes';
import PartialCuid from '@models/PartialCuid';
import { SocialNetwork } from '@models/SocialNetwork';

export interface CreatorsCampaignsMetricsResponse {
  totalRevenue?: number;
  revenueCurrency?: string;
}

export interface CreatorsCampaignsBasicRequest {
  campaignSlugName: string;
}

export interface CreatorsCampaignsMembersRequest
  extends CreatorsCampaignsBasicRequest {
  excludeExtraDetails: boolean;
  networks: SocialCreatorsPlatforms[];
  pageNumber: number | undefined;
  pageSize: number | undefined;
  populateWithCreatorProfile: boolean | undefined;
  searchTerm: string | undefined;
}

export interface ActiveCreatorsCampaignsListRequest {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  from?: number;
  to?: number;
}

export interface CreatorsCampaignsUpdateCreatorMetadataRequest
  extends CreatorsCampaignsBasicRequest {
  creator: {
    username: string;
    network: SocialNetwork;
    shippingDetails?: CreatorShippingDetails;
    metadata?: CreatorsCampaignsMemberMetadata;
  };
}

export interface CreatorsCampaignsAddCreatorsRequest
  extends CreatorsCampaignsBasicRequest {
  creators: BasicCreatorsInfo[];
}

export enum AffiliateType {
  GOOGLE_ANALYTICS = 'GoogleAnalytics',
  SHOPIFY = 'Shopify',
  IMPACT = 'Impact',
}

export interface AffiliateInfo {
  type?: AffiliateType;
  currency?: string;
  revenue?: number;
}

export interface CreatorAnalytics {
  visits?: number;
  onSiteRevenue?: number;
  onSiteRevenueCurrency?: string;
}

export interface CreatorsCampaignsInfoResponse {
  affiliateType?: AffiliateType;
  id?: string;
  ownerUserId?: string;
  ownerName?: string;
  campaignName?: string;
  campaignRequestId?: string;
  campaignSlugName?: string;
  startDate?: Date;
  endDate?: Date;
  mentions?: string[];
  hashtags?: string[];
  // createdAt and updatedAt are strings as is the format auto generated by mongoose
  // format: ISO date yyyy-mm-ddThh:mm:ss.sssZ
  createdAt?: string;
  updatedAt?: string;
  pendingCreators?: BasicCreatorsInfo[];
  removedContents?: PartialCuid[];
  campaignCurrency?: string;
}

export enum CampaignAssetContextEnum {
  Campaign = 'campaign',
}

export interface UploadCampaignAssetsRequest {
  campaignSlugName: string;
  context: CampaignAssetContextEnum;
  contextId: string;
  field: CampaignAssetFieldsEnum;
  files: File[];
  step: CreatorsCampaignWorkflowStep | CreatorsCampaignMessagesStep;
}

export interface UploadCampaignAssetsResponse {
  uploadedAssetUrls?: string[];
  failedAssetNames?: string[];
}

export interface CampaignWorkflowProduct {
  brand?: string;
  campaignSlugName?: string;
  category?: string;
  createdAt?: string;
  description?: string;
  displayProductId?: string;
  id?: string;
  image?: string;
  name?: string;
  orderEligibility?: CampaignProductOrderEligibility;
  pdpUrl?: string;
  price?: number;
  sourceProductId?: string;
  quantity?: number;
  source?: CampaignRequestProductSource;
  storeType?: CreatorCampaignProductStoreType;
  updatedAt?: string;
  variants?: CreatorCampaignsRequestProductVariant[];
}

export enum CampaignProductOrderEligibility {
  ELIGIBLE = 'eligible',
  INELIGIBLE = 'ineligible',
}

export interface CampaignWorkflowProductGift {
  enabled?: boolean;
  numberOfProducts?: number;
  maxNumberOfProducts?: number;
  maxCostOfProducts?: number;
  products?: CampaignWorkflowProduct[];
}

interface CreatorsCampaignsWorkflowStorePricingRule {
  description?: string;
  enabled?: boolean;
  pricingRuleId?: string;
}

export interface CreatorsCampaignsWorkflowStoreConfiguration {
  pricingRule?: CreatorsCampaignsWorkflowStorePricingRule;
}

export interface CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration {
  destinationUrl?: string;
  enabled?: boolean;
}
export interface CreatorsCampaignsWorkflowImpactField {
  id?: string;
  name?: string;
}

export interface CreatorsCampaignsWorkflowImpactConfiguration {
  deal?: CreatorsCampaignsWorkflowImpactField;
  enabled?: boolean;
  program?: CreatorsCampaignsWorkflowImpactField;
  term?: CreatorsCampaignsWorkflowImpactField;
}

export interface CreatorsCampaignWorflowTaskItem {
  guidance: string;
  title: string;
}

export interface CreatorsCampaignsWorkflowResponse {
  id?: string;
  campaignSlugName?: string;
  campaignCurrency?: string;
  campaignBrief?: {
    title?: string;
    description?: string;
  };
  fixedPay?: {
    hideDefaultFromCreators?: boolean;
    collectPaymentDetails?: boolean;
    currency?: string;
    enabled?: boolean;
    proposeCostEnabled?: boolean;
    value?: number;
  };
  socoProductGift?: CampaignWorkflowProductGift;
  storeConfigurations?: CreatorsCampaignsWorkflowStoreConfiguration;
  influencersCommission?: InfluencersCommission;
  googleAnalyticsConfiguration?: CreatorsCampaignsWorkflowGoogleAnalyticsConfiguration;
  impact?: CreatorsCampaignsWorkflowImpactConfiguration;
  socoInfluencerTasks?: {
    enabled?: boolean;
    tasks?: {
      instagramTasks?: {
        post?: CreatorsCampaignWorflowTaskItem & { shareAdCode?: boolean };
        reel?: CreatorsCampaignWorflowTaskItem & { shareAdCode?: boolean };
        story?: CreatorsCampaignWorflowTaskItem & { shareAdCode?: boolean };
      };
      tiktokTasks?: {
        brandName?: string;
        sparkAdsDuration?: string;
        videos?: CreatorsCampaignWorflowTaskItem[];
      };
      youtubeTasks?: {
        videos?: CreatorsCampaignWorflowTaskItem[];
      };
      assets?: { url: string }[];
    };
  };
  contentRights?: {
    description?: string;
    enabled?: boolean;
    expiryDate?: Date;
    source?: ContentRightsSources;
  };
  contentReviewEnabled?: boolean;
  campaignImageUrl?: string;
  revisionUpdated?: string;
  assets?: { url: string; hash?: string; size?: number; type?: string }[];
  isWorkflowConfigured?: boolean;
  isPublished?: boolean;
  isPreviewVersion?: boolean;
  isCollectPaymentDetailsEnabled?: boolean;
}

export interface CreatorsCampaignsWorkflowPreviewTokenResponse {
  token?: string;
}

export enum CampaignWorkflowProductsListSortCriteria {
  Quantity = 'quantity',
  Price = 'price',
  CreatedAt = 'createdAt',
}

export enum CampaignWorkflowProductsListSortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

export interface CampaignWorkflowProductsListRequest {
  campaignSlugName?: string;
  fetchAll?: boolean;
  pageNumber?: number;
  pageSize?: number;
  published?: boolean;
  searchTerm?: string;
  sortCriteria?: CampaignWorkflowProductsListSortCriteria;
  sortDirection?: CampaignWorkflowProductsListSortDirection;
}

export interface CampaignWorkflowProductsListResponse {
  data: CampaignWorkflowProduct[];
  pageNumber: number;
  hasNextPage: boolean;
  totalItems: number;
  totalPages: number;
}

export enum CreatorsCampaignWorkflowStep {
  Basics = 'campaign-brief',
  ContentRights = 'content-rights',
  Payment = 'fixed-pay',
  Revision = 'revision',
  Tasks = 'soco-tasks',
  Products = 'soco-product-gifting',
}

export enum CreatorsCampaignMessagesStep {
  Messages = 'soco-campaign-messages',
}

export enum CampaignAssetFieldsEnum {
  CAMPAIGN_IMAGE = 'campaign_image',
  CAMPAIGN_IMAGE_IDEAS = 'campaign_image_ideas',
  PRODUCT_IMAGE = 'product_image',
  PRODUCT_VARIANT_IMAGE = 'product_variant_image',
  CREATOR_INSPIRATIONS = 'creator_inspirations',
  CAMPAIGN_MESSAGES_ATTACHMENTS = 'campaign_messages_attachments',
}

export enum ContentRightsSources {
  BV = 'bv',
  CUSTOM = 'custom',
}

export enum CampaignWorkflowAffiliateConfigurationEnum {
  GoogleAnalytics = 'google_analytics',
  Shopify = 'shopify',
  Impact = 'impact',
}

export interface CreatorsCampaignsWorkflowUpdateRequest {
  campaignSlugName: string;
  workflow: CreatorsCampaignsWorkflowResponse;
  step: CreatorsCampaignWorkflowStep;
  campaignImage?: File;
  assets?: File[];
  shouldSaveRevision?: boolean;
}

export interface CreatorsCampaignsDeleteCreatorsRequest
  extends CreatorsCampaignsBasicRequest {
  creators: BasicCreatorsInfo[];
}

export interface CreatorsCampaignsDeletePendingCreatorsRequest
  extends CreatorsCampaignsBasicRequest {
  usernames: string[];
}

export interface CreatorsCampaignsDeleteContentRequest
  extends CreatorsCampaignsBasicRequest {
  contentIds: PartialCuid[];
}

export interface CreatorsCampaignsMembersListResponse {
  creators?: CreatorsCampaignsMember[];
  hasMoreCreators?: boolean;
  isSensitiveCampaignLinksWarningDisabled?: boolean;
  page?: number;
  totalResultCount?: number;
}

export interface CreatorsCampaignsReportRequest
  extends CreatorsCampaignsBasicRequest {
  mediaIds: string[];
  isAggregation: boolean;
  platform: SocialCreatorsPlatforms | 'CROSS';
}

export interface CreatorsCampaignsReportResponse {
  aggregatedCommentAnalysis?: CommentAnalysis;
  mediaComments?: MediaComment[];
  commentAnalysis?: { id?: string; commentAnalysis?: CommentAnalysis }[];
}

export type CreatorsCampaignsReportQueryResponse =
  | (CreatorsCampaignsReportResponse & {
      contentItems: IContentLibraryItem[];
    })
  | undefined;

export type MediaCommentSentiment =
  | 'positive'
  | 'slightly positive'
  | 'negative'
  | 'slightly negative'
  | 'neutral';

export interface MediaComment {
  id?: string;
  mediaId?: string;
  username?: string;
  sentiment?: MediaCommentSentiment;
  text?: string;
  mediaPk?: string;
}

export interface CommentAnalysis {
  total?: number;
  averageLength?: number;
  emojiOnlyCount?: number;
  hasMentionCount?: number;
  neutralCount?: number;
  positiveCount?: number;
  negativeCount?: number;
}

export interface CreatorsCampaignsMemberInfo {
  aggregateResponse?: CreatorsCampaignsMemberAggregates;
  followers?: number;
  id?: string;
  isRegistered?: boolean;
  source?: SourceType;
  name?: string;
  network?: SocialCreatorsPlatforms;
  profilePictureUrl?: string;
  username?: string;
}

export interface CreatorsCampaignsMember {
  affiliate?: AffiliateInfo;
  creator?: CreatorsCampaignsMemberInfo;
  creatorMetadata?: CreatorsCampaignsMemberMetadata;
  postCount?: number;
  storyCount?: number;
  analytics?: CreatorAnalytics;
  creatorPortalProfile?: CreatorPortalProfile;
}

export interface CreatorPortalProfile {
  id: string;
  email: string;
}

export interface CreatorsCampaignsMemberAggregates {
  mediaCount?: number;
  impressions?: number;
  followerEngagementRate?: number;
  earnedMediaValue?: number;
  currency?: string;
}

export interface AffiliateCode {
  code?: string;
  url?: string;
}

export interface CampaignMemberFixedPay {
  value?: number;
  currency?: string;
  enabled?: boolean;
  comment?: string;
  proposeCost?: number;
}

export interface CampaignFixedPayRequest {
  value?: number;
  currency?: string;
}

export interface CreatorsCampaignsMemberMetadata {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  fixedPay?: CampaignMemberFixedPay;
  shippingDetails?: CreatorShippingDetails;
  affiliateCode?: AffiliateCode;
}

export interface CreatorsCampaignsProposalsRequest
  extends CreatorsCampaignsBasicRequest {
  pageNumber: number | undefined;
  pageSize: number | undefined;
  searchTerm: string | undefined;
}

export interface CreatorsCampaignsProposalsListResponse {
  data?: CreatorsCampaignsProposal[] | [];
  hasNextPage?: boolean;
  pageNumber?: number;
  totalItems?: number;
  totalPages?: number;
}
export interface CreatorsCampaignsProposal {
  creator?: CreatorInfo;
  creatorNotes?: string;
  fixedPay?: FixedPay;
  orderCreationStatus?: CreatorsCampaignOrderCreationStatus;
  orderIds?: string[];
  proposalStatus?: string;
  receivedOn?: string;
  shippingAddress?: {
    line1?: string;
    line2?: string;
    country?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
}

export interface CreatorInfo {
  fullname?: string;
  username?: string;
  profilePicture?: string;
  platform?: SocialCreatorsPlatforms;
  id?: string;
}

export interface CreatorShippingDetails {
  address?: CreatorAddress;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface CreatorAddress {
  city?: string;
  country?: string;
  line1?: string;
  line2?: string;
  postalCode?: string;
  state?: string;
}

export interface UpsertCreatorsCampaignRequest {
  campaignSlugName?: string;
  campaignName?: string;
  hashtags?: string[];
  mentions?: string[];
  startDate?: number;
  endDate?: number;
}

export interface DuplicateCreatorsCampaignRequest {
  campaignName: string;
  originalCampaignSlugName: string;
  sectionsToDuplicate: string[];
}

export interface UpsertCreatorsCampaignResponse {
  campaignSlugName: string;
}

export interface CreatorsCampaignsAddedCreators {
  username?: string;
  network?: SocialCreatorsPlatforms;
}

export interface CreatorsCampaignsAddCreatorsResponse {
  added: CreatorsCampaignsAddedCreators[];
  pending: CreatorsCampaignsAddedCreators[];
}

export enum crossPlatforms {
  Instagram = 'instagram',
  TikTok = 'tiktok',
  YouTube = 'youtube',
}

export enum CreatorsCampaignOrderCreationStatus {
  Created = 'created',
  NotApplicable = 'not_applicable',
  Pending = 'pending',
}

// TODO: PD-289471 -  Create a Tiny Type for campaign slug name
export interface CreatorsCampaignListItem {
  affiliateType?: AffiliateType;
  ownerName?: string;
  campaignName?: string;
  campaignSlugName?: string;
  startDate?: Date;
  endDate?: Date;
  createdAt?: string;
  creatorsCount?: number;
  crossPlatforms?: crossPlatforms[];
  totalRevenue?: number;
  revenueCurrency?: string;
  clicks?: number;
  onSiteRevenue?: number;
  onSiteRevenueCurrency?: string;
}

export type CreatorsCampaignListResponse =
  PaginatedResponse<CreatorsCampaignListItem>;

export interface RequestSparkCodeRequest {
  campaignSlugName: string;
  itemId: ItemId;
  authorizationDays: number;
}

export interface CreatorPortalLinksResponse {
  email?: string;
  name?: string;
  username?: string;
  campaignLink?: string;
}

export interface GetCampaignSparkCodeRequest {
  campaignSlugName: string;
  videoId: ItemId;
  refresh?: boolean;
}

export enum TTOCampaignVideoLinkRequestStatus {
  IN_REVIEW = 'IN_REVIEW',
  APPROVE = 'APPROVE',
  REJECTED = 'REJECTED',
  REVOKED = 'REVOKED',
}

export interface LinkRequest {
  status?: TTOCampaignVideoLinkRequestStatus;
  lastRemindedAt?: Date;
  requestCount?: number;
  linkRequestId?: string;
}

export interface LinkTiktokVideoToTTOCampaignRequest {
  campaignSlugName?: string;
  campaignId: string;
  videoId: ItemId;
}

export interface CampaignSparkCodesInfo {
  isVideoInTTOCampaign?: boolean;
  campaignId?: string; // if isVideoInCampaign is false, this will be the tto-campaign that has the creator id
  inviteLink?: string; // if isVideoInCampaign is false, this will be the invite link of the tto-campaign that has the creator id
  linkRequest?: LinkRequest; // only present if isVideoInTTOCampaign is false
  sparkCode?: string; // undefined if isVideoInCampaign is false OR creator has not generated spark code for the request
  sparkCodeStatus?: TiktokAuthCodeStatus;
  sparkAuthStatus?: TiktokAuthStatus;
}

export interface LinkTiktokVideoToTTOCampaignResponse {
  id?: string;
  linkRequest?: LinkRequestEntity;
}

export interface LinkRequestEntity {
  status?: TTOCampaignVideoLinkRequestStatus;
}

export interface SparkCodeMedia {
  mediaId: string;
  paidViews: number;
}

export interface SparkCodeMediaResponse {
  medias: SparkCodeMedia[];
}

export enum TiktokAuthStatus { // from tiktok
  ACCEPTED = 'ACCEPTED',
  WAITING = 'WAITING',
  REJECTED = 'REJECTED',
}

export enum TiktokAuthCodeStatus { // from tiktok
  REQUESTED = 'REQUESTED', // affable added for when client requests spark code
  NOT_USED = 'NOT_USED',
  IN_USE = 'IN_USE',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
}

export interface TikTokAdAccountsResponse {
  advertiserInfos?: TikTokAdAccountInfo[];
  scope?: TikTokAdAccountsScope[];
  missingPermissions?: string[];
  name?: string;
  accessToken?: string;
  hasStaleCredentials?: boolean;
}

export enum TikTokAdAccountsScope {
  TIKTOK_ACCOUNTS = 'TikTok Accounts',
  AD_ACCOUNT_MANAGEMENT = 'Ad Account Management',
  TIKTOK_CREATOR_MARKETPLACE = 'TikTok Creator Marketplace (TCM)',
  TIKTOK_CREATOR = 'TikTok Creator',
  AUTHORIZE_TIKTOK_POSTS = 'Authorize TikTok Posts',
  APP_MANAGEMENT = 'App Management',
}

export interface TikTokAdAccountInfo {
  advertiserId?: string;
  advertiserName?: string;
}

export interface ApplySparkCodeRequest {
  sparkCode: string;
  advertiserIds: string[];
}

export interface GetSparkCodeMediaIdsRequest {
  campaignSlugName?: string;
  refresh?: boolean;
}

export interface CreatorsCampaignsAffiliatesListRequest {
  campaignSlugName: string | undefined;
  pageNumber: number | undefined;
  pageSize: number | undefined;
  searchTerm: string | undefined;
  allInfluencers?: boolean;
}

export interface CreatorsCampaignsAffiliatesGAConfigurationRequest {
  campaignSlugName?: string;
  allInfluencers?: boolean;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
}

export interface CreatorsCampaignAffiliateDetailsStats {
  affiliateCode?: string;
  affiliateLink?: string;
  currency?: string;
  ordersCount?: number;
  productsCount?: number;
  recentOrderDate?: string;
  totalCommission?: number;
  totalRevenue?: number;
}

export interface CreatorsCampaignFixedPayStats {
  value?: number;
  currency?: string;
}

export interface CreatorsCampaignAffiliateListCreator {
  affiliateDetails?: CreatorsCampaignAffiliateDetailsStats;
  fixedPay?: CreatorsCampaignFixedPayStats;
  influencerId?: string;
  name?: string;
  network?: SocialCreatorsPlatforms;
  profilePictureUrl?: string;
  username?: string;
}

export interface CreatorsCampaignsAffiliatesListResponse {
  influencers?: CreatorsCampaignAffiliateListCreator[];
  hasMoreInfluencers?: boolean;
  totalPages?: number;
  totalItems?: number;
}

export interface CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationCreator {
  fixedPay?: CreatorsCampaignFixedPayStats;
  creatorId?: string;
  googleAnalyticsConfiguration?: {
    enabled?: boolean;
    destinationUrl?: string;
    shortLink?: string;
  };
  network?: SocialCreatorsPlatforms;
  profilePictureUrl?: string;
  username?: string;
  name?: string;
}

export interface CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationResponse {
  influencers: CreatorsCampaignsAffiliatesGoogleAnalyticsConfigurationCreator[];
  hasMoreInfluencers?: boolean;
  totalPages?: number;
  totalItems?: number;
}

export interface CreatorsCampaignsAffiliateOrderLineItem {
  id?: string;
  name?: string;
  price?: string;
  quantity?: number;
}

export interface CreatorsCampaignsAffiliateOrder {
  affiliateCode?: string;
  commission?: number;
  createdAt?: string;
  currency?: string;
  lineItems?: CreatorsCampaignsAffiliateOrderLineItem[];
  orderId?: string;
  revenue?: number;
}
export interface CreatorsCampaignsAffiliateOrdersResponse {
  hasMoreOrders?: boolean;
  influencersCommission?: {
    collectPaymentDetails?: boolean;
    commissionType?: CreatorsCampaignInfluencerCommissionType;
    commissionValue?: number;
    currency?: string;
  };
  orders?: CreatorsCampaignsAffiliateOrder[];
  totalItems?: number;
  totalPages?: number;
}

export interface CreatorsCampaignsAffiliateOrdersRequest {
  campaignSlugName?: string;
  affiliateCode?: string;
  from?: Date;
  to?: Date;
  pageNumber: number;
  pageSize: number;
}
export interface CreatorsCampaignsAffiliatesAggregateStatsResponse {
  currency?: string;
  timeSeries?: CreatorsCampaignAffiliateStatsTimeSeries[];
  totalOrders?: number;
  totalProducts?: number;
  totalRevenue?: number;
  totalCommission?: number;
}

export interface ExportCreatorPortalLinkCSVRequest {
  campaignSlugName: string;
  creatorsIds?: string[];
}

export interface GetCreatorsCampaignAffiliatesStatsRequest
  extends CreatorsCampaignsBasicRequest {
  granularity?: CreatorsCampaignAffiliateStatsTimeGranularity;
  startDate?: DateString;
  endDate?: DateString;
  // TODO PD-262426: pass the timezone value when ready
  // timeZone?: TimeZoneId;
}

export interface CreatorsCampaignAffiliateStatsTimeSeries {
  date?: DateString;
  metrics?: CreatorsCampaignAffiliateStatsTimeSeriesMetrics;
}

export interface CreatorsCampaignAffiliateStatsTimeSeriesMetrics {
  commission?: number;
  orders?: number;
  products?: number;
  revenue?: number;
}

export enum CreatorsCampaignAffiliateStatsTimeGranularity {
  Day = 'DAY',
  Week = 'WEEK',
  Month = 'MONTH',
}

export interface CreatorPortalInviteLinkResponse {
  link: string;
}

export interface GetCreatorsCampaignsWorkflowRequest
  extends CreatorsCampaignsBasicRequest {
  published?: boolean;
}

export interface ImpactCreatorMapping {
  username?: string;
  platform?: string;
  partnerId?: string;
  affiliateCode?: string;
  affiliateLink?: string;
}

export interface ImpactMappingRequest {
  campaignSlugName: string;
  linkRequests: ImpactCreatorMapping[];
}

export interface ImpactCreatorMappingResponse {
  id?: string;
  influencerId: string;
  partnerId: string;
  platform: string;
}

export interface ImpactCreatorMappingFailure {
  partnerId?: string;
  username?: string;
  platform?: string;
  affiliateCode?: string;
  affiliateLink?: string;
  error?: string;
}

export interface LinkImpactPartnerResponse {
  successfulMappings?: ImpactCreatorMappingResponse[];
  failedMappings?: ImpactCreatorMappingFailure[];
}

export interface PartnerMapping {
  username?: string;
  platform?: string;
}

export interface GetPartnerMappingsRequest {
  campaignSlugName: string;
}

export interface GetPartnerMappingsResponse {
  partnerMappings: PartnerMapping[];
}

export interface GetConnectedImpactPartnersRequest {
  campaignSlugName: string;
  influencerId?: string;
  partnerId?: string;
  platform?: string;
}

export interface GetConnectedImpactPartnersResponse {
  impactPartners?: ImpactCreatorMappingResponse[];
}

export interface UploadCreatorMediaRequest {
  campaignSlugName: string;
  platform: ProfileNetwork;
  mediaCodes: string[];
  username: string;
}

export interface UploadCreatorMediaResponse {
  message?: string;
}

export interface BulkUpdateCreatorMetadata {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  fixedPay?: CampaignMemberFixedPay;
  affiliateCode?: AffiliateCode;
}

export interface CreatorMetadataUpdateItem {
  username: string;
  network: SocialNetwork;
  shippingDetails?: CreatorShippingDetails;
  metadata?: BulkUpdateCreatorMetadata;
}

export interface BulkUpdateCreatorsRequest
  extends CreatorsCampaignsBasicRequest {
  creators: CreatorMetadataUpdateItem[];
}

export interface FieldResult {
  field: 'shipping' | 'metadata' | 'fixedPay';
  status: 'updated' | 'failed' | 'skipped' | 'not_requested';
  message?: string;
}

export interface InfluencerUpdateResult {
  username: string;
  platform: string;
  success: boolean;
  error?: string;
  fieldResults?: FieldResult[];
}

export interface BulkUpdateCreatorsResponse {
  message?: string;
  results?: InfluencerUpdateResult[];
}
